// src/main/content/contests/ContestRaceReplay.js
//
// Animated "race replay" bar chart. Plays through the contest's timeline
// frame-by-frame; bars grow horizontally as employees accumulate value, and
// swap vertical positions as the rankings change.
//
// The animation is built with plain CSS transitions (no Framer Motion or
// other dep) — each bar is absolutely positioned and uses `transform:
// translateY(<rank * ROW_HEIGHT>)` so React can keep the DOM nodes stable
// across re-renders (key=employee.id) while CSS handles the smooth reorder.
//
// Renders nothing if the timeline has < 2 dates (a single-point race isn't
// meaningful). A single-participant contest is still useful: the one bar
// grows day-over-day, which tells their score-accumulation story.

import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import { TIMELINE_COLORS } from "./ContestCumulativeChart";

const ROW_HEIGHT = 42; // px — height of each bar row including its margin
const TOP_N = 8; // most a leaderboard typically benefits from animating
const BASE_FRAME_MS = 600; // playback speed at 1x — divided by speed to compute interval
const SPEED_OPTIONS = [1, 2, 4];

function formatValue(value, format) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  const v = Number(value);
  if (format === "dollars")
    return `$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  if (format === "hours") return `${v.toFixed(1)}h`;
  // Percent of the location's goal, floored so 99.6% never reads "100%".
  if (format === "goal_percent") return `${Math.floor(v)}%`;
  // Ratio metrics need both decimals — 1-decimal rounding would collapse
  // nearby rates (0.46 vs 0.52 → "0.5").
  if (format === "rate") return v.toFixed(2);
  return v.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0][0] || "?").toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

export default function ContestRaceReplay({
  timeline,
  metricFormat,
  metricLabel,
  showStoreTag = false,
  lowerIsBetter = false,
  winnersCount = 1,
  // "hit_target" with a goalValue: trophies go to everyone at or past the
  // goal instead of the top winnersCount (same rule as the leaderboard).
  winnerMode = "top_n",
  // Goal metrics: the value every bar races toward (100 = 100% of goal).
  // Drawn as a line, and the scale always leaves room to show it.
  goalValue = null,
}) {
  const employees = (timeline?.employees || []).slice(0, TOP_N);
  const dates = timeline?.dates || [];
  const dateCount = dates.length;
  const isEligible = dateCount >= 2 && employees.length >= 1;

  // Frame index: which date we're currently showing. Start at the final
  // frame so the page reflects current standings; pressing play rewinds.
  const [idx, setIdx] = useState(Math.max(0, dateCount - 1));
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const intervalRef = useRef(null);

  // Reset frame index when the contest timeline changes underneath us
  // (e.g., user navigates to a different contest without unmounting).
  useEffect(() => {
    setIdx(Math.max(0, dateCount - 1));
    setIsPlaying(false);
  }, [dateCount]);

  // Playback loop. Tears itself down whenever inputs change so we never
  // double up intervals.
  useEffect(() => {
    if (!isPlaying || !isEligible) return undefined;
    const intervalMs = Math.max(80, BASE_FRAME_MS / speed);
    intervalRef.current = setInterval(() => {
      setIdx((prev) => {
        if (prev >= dateCount - 1) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, intervalMs);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, speed, dateCount, isEligible]);

  // Global max across the entire timeline (any employee, any date) so bar
  // widths grow naturally as the race progresses without weird re-scaling
  // when someone passes the current leader.
  const globalMax = useMemo(() => {
    let m = 0;
    for (const e of employees) {
      for (const v of e.cumulative || []) {
        if (v > m) m = v;
      }
    }
    if (goalValue != null && goalValue > m) m = goalValue;
    return m || 1;
  }, [employees, goalValue]);
  const goalLeftPct =
    goalValue != null ? Math.min(100, (goalValue / globalMax) * 100) : null;

  // For the current frame, rank order in the metric's "better" direction.
  // Higher-is-better: bv - av (descending = leader first). Lower-is-better:
  // av - bv (ascending = lowest cumulative first). Without this, the
  // animation would visually crown the wrong leader for contests like
  // "fewest late clock-ins" — the bar showing the WORST count would sit at
  // the top instead of the actual leaderboard #1.
  const rankByEmployeeId = useMemo(() => {
    const safeIdx = Math.min(idx, dateCount - 1);
    const ranked = [...employees].sort((a, b) => {
      const av = a.cumulative?.[safeIdx] ?? 0;
      const bv = b.cumulative?.[safeIdx] ?? 0;
      return lowerIsBetter ? av - bv : bv - av;
    });
    const map = {};
    ranked.forEach((e, i) => {
      map[e.id] = i;
    });
    return map;
  }, [employees, idx, dateCount, lowerIsBetter]);

  if (!isEligible) return null;

  const safeIdx = Math.min(idx, dateCount - 1);
  const currentDate = dates[safeIdx];
  const atEnd = safeIdx >= dateCount - 1;

  const handlePlayPause = () => {
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }
    if (atEnd) setIdx(0); // rewind on play-from-end
    setIsPlaying(true);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden h-full">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TimelapseIcon className="text-violet-600" style={{ fontSize: 18 }} />
          <h3 className="text-base font-semibold text-slate-900">Race Replay</h3>
        </div>
        <div className="text-xs text-slate-500">Watch how the leaderboard unfolded</div>
      </div>

      <div className="px-5 pt-4 pb-2 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
            Showing
          </div>
          <div className="text-base font-bold text-slate-900">
            {dayjs(currentDate).format("MMM D, YYYY")}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handlePlayPause}
            className="w-9 h-9 rounded-full bg-violet-700 hover:bg-violet-800 text-white flex items-center justify-center shadow-sm"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {/* Always show ``PlayArrowIcon`` when paused, regardless of whether
                we're at the end of the timeline. The replay starts at the
                FINAL frame (so the page initially mirrors the leaderboard),
                which means the button is always atEnd for first-time
                viewers — ``ReplayIcon`` would read as "watch again" when
                they haven't watched it once yet. The click handler still
                rewinds to idx 0 when atEnd, so behavior is unchanged. */}
            {isPlaying ? (
              <PauseIcon style={{ fontSize: 18 }} />
            ) : (
              <PlayArrowIcon style={{ fontSize: 20 }} />
            )}
          </button>
          <div className="flex items-center gap-0.5 bg-slate-50 rounded-full p-0.5 border border-slate-200">
            {SPEED_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setSpeed(s)}
                className={`px-2 py-0.5 text-xs rounded-full font-medium transition-colors ${
                  speed === s
                    ? "bg-white shadow text-violet-700"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {s}x
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-5 pb-1">
        <input
          type="range"
          min={0}
          max={Math.max(0, dateCount - 1)}
          value={safeIdx}
          onChange={(e) => {
            setIsPlaying(false);
            setIdx(parseInt(e.target.value, 10));
          }}
          className="w-full accent-violet-700 cursor-pointer"
        />
        <div className="flex items-center justify-between text-[11px] text-slate-400 mt-1">
          <span>{dayjs(dates[0]).format("MMM D")}</span>
          <span>{dayjs(dates[dateCount - 1]).format("MMM D")}</span>
        </div>
      </div>

      {/* The bars themselves. Absolute positioning lets us animate the
          translateY independently of the DOM order — React keeps the bars in
          their initial order so keys stay stable; CSS handles the visible
          reorder. */}
      <div className="px-5 pb-4 pt-2">
        <div
          className="relative"
          style={{ height: employees.length * ROW_HEIGHT }}
        >
          {employees.map((emp, origIdx) => {
            const rank = rankByEmployeeId[emp.id] ?? origIdx;
            const value = emp.cumulative?.[safeIdx] ?? 0;
            const widthPct = Math.max(0, Math.min(100, (value / globalMax) * 100));
            const color = TIMELINE_COLORS[origIdx % TIMELINE_COLORS.length];
            const isLeader = rank === 0;
            // Trophy renders for everyone currently in the prize positions,
            // matching the leaderboard's "Top N earn rewards" treatment.
            // Re-evaluated per frame so the trophy moves with the standings
            // as the race unfolds.
            const isWinner =
              goalValue != null && winnerMode === "hit_target"
                ? value >= goalValue
                : rank < winnersCount;
            return (
              <div
                key={emp.id}
                className="absolute left-0 right-0 flex items-center gap-2.5"
                style={{
                  transform: `translateY(${rank * ROW_HEIGHT}px)`,
                  transition: "transform 500ms cubic-bezier(0.4, 0, 0.2, 1)",
                  height: ROW_HEIGHT - 6,
                  top: 0,
                }}
              >
                <div className="flex items-center justify-center w-5 text-xs font-semibold text-slate-500">
                  {rank + 1}
                </div>
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-semibold shrink-0"
                  style={{ backgroundColor: color }}
                >
                  {initials(emp.full_name)}
                </div>
                <div className="w-28 shrink-0 min-w-0">
                  {/* Flex row so a long name truncates with an ellipsis but
                      the trophy never gets clipped along with it. Putting
                      ``truncate`` on the outer container alone (as the old
                      structure did) made the trophy disappear whenever the
                      name overflowed — same pattern we fixed in the
                      LeaderboardRow earlier. */}
                  <div className="flex items-center gap-1 text-sm font-medium text-slate-900 min-w-0">
                    <span className="truncate min-w-0">{emp.full_name}</span>
                    {isWinner && (
                      <EmojiEventsIcon
                        className="text-amber-400 shrink-0"
                        style={{ fontSize: 12 }}
                      />
                    )}
                  </div>
                  {showStoreTag && emp.store_name && (
                    <div className="text-[10px] text-slate-400 -mt-0.5 truncate">
                      {emp.store_name}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0 relative">
                  {goalLeftPct !== null && (
                    <div
                      className="absolute -top-1 -bottom-1 border-l-2 border-dashed border-slate-400"
                      style={{ left: `${goalLeftPct}%` }}
                      title="Goal"
                      aria-hidden
                    />
                  )}
                  <div
                    className="h-6 rounded-r-md transition-all duration-500 ease-out"
                    style={{
                      width: `${widthPct}%`,
                      backgroundColor: color,
                      // Keep the leader's bar at full opacity so the #1
                      // position still reads as primary — winners 2..N stay
                      // slightly muted, same as the non-winners.
                      opacity: isLeader ? 1 : 0.85,
                    }}
                  />
                </div>
                <div className="text-sm font-semibold text-slate-900 w-16 text-right shrink-0">
                  {formatValue(value, metricFormat)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
